import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, XCircle, ShieldCheck, Sparkles } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SEO from "@/components/site/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function Row({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1 py-3 border-b border-[#E8DFD8] last:border-b-0">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#8C7A70]">{label}</span>
      <span className="text-sm font-semibold text-[#2A1C14]">{value}</span>
    </div>
  );
}

export default function BrokerVerify() {
  const { token } = useParams();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState("");
  const [verifierName, setVerifierName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/broker-verify/${token}`, { withCredentials: false });
        setRec(data);
      } catch (err) {
        setError(err?.response?.data?.detail || "This verification link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (decision) => {
    if (!verifierName.trim()) {
      toast.error("Please enter your name so we can record who signed off.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/broker-verify/${token}`,
        { decision, notes: notes.trim() || null, verifier_name: verifierName.trim() },
        { withCredentials: false }
      );
      setOutcome({ decision, at: data.decided_at });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your response. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="broker-verify-loading">
        <SEO title="Broker Verification" path={`/broker-verify/${token}`} />
        <p className="text-[#5A3B31]">Loading transaction details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="broker-verify-error">
        <SEO title="Broker Verification" path={`/broker-verify/${token}`} />
        <XCircle size={40} strokeWidth={1.25} className="mx-auto text-rose-400" />
        <h1 className="font-serif-lux text-4xl mt-6 text-[#2A1C14]">Link unavailable</h1>
        <p className="mt-4 text-[#5A3B31] leading-relaxed">{error}</p>
        <Link to="/" className="inline-block mt-8 text-sm underline text-[#5A3B31]">
          Return to Hannah Howard Funding
        </Link>
      </div>
    );
  }

  if (outcome) {
    const ok = outcome.decision === "verified";
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="broker-verify-success">
        <SEO title="Broker Verification Received" path={`/broker-verify/${token}`} />
        <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${ok ? "bg-[#F3D8DA] text-[#2A1C14]" : "bg-rose-100 text-rose-800"}`}>
          {ok ? <CheckCircle2 size={40} strokeWidth={1.5} /> : <XCircle size={40} strokeWidth={1.5} />}
        </div>
        <h1 className="font-serif-lux text-4xl mt-8 text-[#2A1C14]">
          {ok ? "Thank you — verification received." : "Response recorded."}
        </h1>
        <p className="mt-4 text-[#5A3B31] leading-relaxed">
          {ok
            ? "We've recorded your confirmation for this transaction. Hannah Howard Funding will proceed with underwriting review and reach out with next steps."
            : "We've recorded that this transaction should not proceed. Hannah Howard Funding has been notified and will follow up if needed."}
        </p>
        <p className="text-xs text-[#8C7A70] mt-6">
          Reference: {token.slice(0, 8)}
        </p>
      </div>
    );
  }

  const isAlreadyDecided = rec?.status && rec.status !== "pending";
  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="broker-verify-page">
      <SEO title="Broker Verification" description="Confirm a real estate commission advance transaction." path={`/broker-verify/${token}`} />
      <div className="text-center mb-8">
        <span className="overline flex items-center justify-center gap-2">
          <ShieldCheck size={14} strokeWidth={1.5} /> Broker Verification
        </span>
        <h1 className="font-serif-lux text-4xl sm:text-5xl mt-3 text-[#2A1C14]">
          Confirm this transaction
        </h1>
        <p className="mt-4 text-[#5A3B31] max-w-md mx-auto leading-relaxed">
          {rec?.applicant_name} has applied for a real estate commission advance with Hannah Howard Funding. Please review the details below and confirm whether this transaction is legitimate and in good standing.
        </p>
      </div>

      <div className="lux-card p-8" data-testid="broker-verify-details">
        <div className="mb-2">
          <span className="overline">Transaction Details</span>
        </div>
        <Row label="Applicant" value={rec.applicant_name} />
        <Row label="Property Address" value={rec.property_address} />
        <Row label="Expected Closing" value={rec.expected_closing_date} />
        <Row label="Expected Commission" value={fmt(rec.expected_commission)} />
        <Row label="Advance Requested" value={fmt(rec.advance_amount_requested)} />
        <Row label="Broker of Record" value={`${rec.broker_first_name || ""} ${rec.broker_last_name || ""}`.trim() || "—"} />
      </div>

      {isAlreadyDecided ? (
        <div className="mt-6 lux-card p-6 text-center" data-testid="broker-verify-already">
          <p className="text-sm text-[#5A3B31]">
            This verification was already submitted (<strong>{rec.status}</strong>) on{" "}
            {new Date(rec.decided_at).toLocaleString()}. Thank you.
          </p>
        </div>
      ) : (
        <form
          className="mt-6 lux-card p-8 space-y-5"
          onSubmit={(e) => e.preventDefault()}
          data-testid="broker-verify-form"
        >
          <div>
            <Label htmlFor="verifier-name" className="text-xs font-semibold uppercase tracking-wider text-[#5A3B31]">
              Your full name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="verifier-name"
              data-testid="bv-name"
              className="mt-2"
              value={verifierName}
              onChange={(e) => setVerifierName(e.target.value)}
              placeholder="e.g. Jane Smith, Managing Broker"
            />
          </div>
          <div>
            <Label htmlFor="verifier-notes" className="text-xs font-semibold uppercase tracking-wider text-[#5A3B31]">
              Notes (optional)
            </Label>
            <Textarea
              id="verifier-notes"
              data-testid="bv-notes"
              className="mt-2 min-h-[90px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything Hannah Howard Funding should know about this transaction"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              disabled={submitting}
              onClick={() => submit("declined")}
              data-testid="bv-decline"
              className="flex-1 rounded-full border border-rose-300 text-rose-700 py-3 px-5 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50"
            >
              This transaction should not proceed
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => submit("verified")}
              data-testid="bv-verify"
              className="flex-1 btn-primary justify-center disabled:opacity-50"
            >
              <Sparkles size={16} strokeWidth={1.5} /> Confirm — this transaction is legitimate
            </button>
          </div>
          <p className="text-[11px] text-[#8C7A70] leading-relaxed pt-2">
            Your response is recorded with your name, timestamp, and IP for audit purposes. Only Hannah Howard Funding staff can view this record.
          </p>
        </form>
      )}
    </div>
  );
}
